import { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

// Assuming you have icons in your project or a library like react-icons
import { 
  BsBuilding, 
  BsCalendarDate, 
  BsCoin, 
  BsPencil, 
  BsPaperclip, 
  BsFileEarmarkText, 
  BsCreditCardFill 
} from 'react-icons/bs';

function ExpenseDetailsForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { expenseId: expenseIdFromUrl } = useParams();
  const expenseIdFromState = location.state?.expense_id;

  const expenseId = expenseIdFromState || expenseIdFromUrl || '';

  const decodeURIComponentSafe = (val) => {
    try { return decodeURIComponent(val); } catch { return val; }
  };

  const [formData, setFormData] = useState({
    vendor_name: '',
    cost: '',
    currency: 'INR',
    date: '',
    payment_terms: '',
    description: '',
    quote: null
  });

  // Line items state (integrated from ExpenseItems)
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemsSaving, setItemsSaving] = useState(false);
  const [itemsMaster, setItemsMaster] = useState([]); // [{id, name, sku, uom}]
  const [rows, setRows] = useState([
    { id: crypto.randomUUID(), item_id: '', free_text_name: '', uom: 'each', qty: '', unit_price: '', type: 'Inventory' }
  ]);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAdd, setQuickAdd] = useState({ name: '', sku: '', uom: 'each', category: 'Stationery', reorder_level: '' });

  const grandTotal = useMemo(() => {
    return rows.reduce((sum, r) => {
      const qty = Number(r.qty) || 0;
      const price = Number(r.unit_price) || 0;
      return sum + qty * price;
    }, 0);
  }, [rows]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (stayOnPage = false) => {
    try {
      // Validate line items before saving vendor details
      const itemsError = validateRows();
      if (itemsError) {
        alert(itemsError);
        return;
      }
      const form = new FormData();
      form.append('vendor_name', formData.vendor_name);
      form.append('cost', formData.cost);
      form.append('currency', formData.currency || 'INR');
      form.append('date', formData.date);
      form.append('payment_terms', formData.payment_terms);
      form.append('description', formData.description);
      const d1 = decodeURIComponentSafe(expenseId);
      const normId = String(d1).replace(/%252F/gi, '%2F').replace(/%2F/gi, '/');
      form.append('expense_id', normId);
      if (formData.quote) form.append('quote', formData.quote);

      const vendorRes = await api.post('/api/expenses/expense-details', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Save line items immediately after vendor details
      const payload = {
        lines: rows.map(r => ({
          item_id: r.type === 'Inventory' ? r.item_id : null,
          free_text_name: r.type !== 'Inventory' ? r.free_text_name.trim() : null,
          uom: r.uom,
          ordered_qty: Number(r.qty),
          unit_price: Number(r.unit_price),
          type: r.type,
        })),
        vendor_detail_id: vendorRes?.data?.id || null,
      };
      await api.post(`/api/expenses/items/by-id`, {
        expenseId: normId,
        ...payload,
      });

      alert('Vendor details and line items saved');

      if (!stayOnPage) navigate('/expenses');
      else {
        setFormData({ vendor_name: '', cost: '', currency: 'INR', date: '', payment_terms: '', description: '', quote: null });
        // Reset items rows for adding next vendor's lines
        setRows([{ id: crypto.randomUUID(), item_id: '', free_text_name: '', uom: 'each', qty: '', unit_price: '', type: 'Inventory' }]);
      }
    } catch (error) {
      console.error('Error saving vendor details:', error);
      alert('Failed to save vendor details');
    }
  };

  // Load items master for line items
  useEffect(() => {
    const loadItems = async () => {
      try {
        const res = await api.get('/api/inventory/items');
        // Create a map to store unique items by name
        const uniqueItemsMap = new Map();
        (res.data || []).forEach(item => {
          if (item.name && !uniqueItemsMap.has(item.name)) {
            uniqueItemsMap.set(item.name, item);
          }
        });
        // Convert map values back to array
        const uniqueItems = Array.from(uniqueItemsMap.values());
        setItemsMaster(uniqueItems);
      } catch (e) {
        console.error('Failed to load items master', e);
      } finally {
        setItemsLoading(false);
      }
    };
    loadItems();
  }, []);

  const updateRow = (id, patch) => {
    setRows(prev => {
      // If we're updating item_id, find the selected item and update UOM
      if ('item_id' in patch) {
        const selectedItem = itemsMaster.find(item => item.id == patch.item_id);
        if (selectedItem) {
          // Clean up UOM value (remove any extra whitespace or newlines)
          const cleanUom = selectedItem.uom ? selectedItem.uom.trim() : 'each';
          return prev.map(r => 
            r.id === id 
              ? { 
                  ...r, 
                  ...patch, 
                  uom: cleanUom  // Set UOM from selected item
                } 
              : r
          );
        } else if (patch.item_id === '') {
          // If item is cleared, reset UOM to default
          return prev.map(r => 
            r.id === id 
              ? { ...r, ...patch, uom: 'each' } 
              : r
          );
        }
      }
      return prev.map(r => (r.id === id ? { ...r, ...patch } : r));
    });
  };

  const addRow = () => {
    setRows(prev => [...prev, { id: crypto.randomUUID(), item_id: '', free_text_name: '', uom: 'each', qty: '', unit_price: '', type: 'Inventory' }]);
  };

  const removeRow = (id) => {
    setRows(prev => (prev.length > 1 ? prev.filter(r => r.id !== id) : prev));
  };

  const handleQuickAddSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: quickAdd.name.trim(),
        sku: quickAdd.sku.trim(),
        uom: quickAdd.uom.trim() || 'each',
        category: quickAdd.category || 'Other',
        reorder_level: quickAdd.reorder_level ? Number(quickAdd.reorder_level) : 0
      };
      if (!payload.name) return;
      const res = await api.post('/api/inventory/items', payload);
      const created = res.data;
      setItemsMaster(prev => [created, ...prev]);
      setRows(prev => {
        const copy = [...prev];
        const idx = copy.findIndex(r => !r.item_id && r.type === 'Inventory');
        if (idx !== -1) copy[idx] = { ...copy[idx], item_id: created.id, free_text_name: '' };
        return copy;
      });
      setShowQuickAdd(false);
      setQuickAdd({ name: '', sku: '', uom: 'each', category: 'Stationery', reorder_level: '' });
    } catch (e) {
      console.error('Quick add failed', e);
      
      // Handle specific error cases
      if (e.response?.status === 409) {
        // Item already exists - use the existing item
        const existingItem = e.response.data.item;
        if (existingItem) {
          alert(`Item "${quickAdd.name}" already exists. Using existing item.`);
          // Add existing item to the current row
          setRows(prev => {
            const copy = [...prev];
            const idx = copy.findIndex(r => !r.item_id && r.type === 'Inventory');
            if (idx !== -1) copy[idx] = { ...copy[idx], item_id: existingItem.id, free_text_name: '' };
            return copy;
          });
          setShowQuickAdd(false);
          setQuickAdd({ name: '', sku: '', uom: 'each', category: 'Stationery', reorder_level: '' });
        } else {
          alert('This item already exists. Please select it from the dropdown.');
          setShowQuickAdd(false);
        }
      } else if (e.response?.data?.error) {
        alert(`Failed to add item: ${e.response.data.error}`);
      } else {
        alert('Failed to add item. Please try again.');
      }
    }
  };

  const validateRows = () => {
    for (const r of rows) {
      const qty = Number(r.qty);
      const price = Number(r.unit_price);
      if (r.type === 'Inventory' && !r.item_id) return 'Please select an inventory item.';
      if (r.type !== 'Inventory' && !r.free_text_name.trim()) return 'Please enter item name for non-inventory row.';
      if (!(qty > 0)) return 'Quantity must be greater than zero.';
      if (!(price >= 0)) return 'Unit price must be zero or greater.';
    }
    return null;
  };

  const handleSaveItems = async () => {
    const error = validateRows();
    if (error) return alert(error);
    setItemsSaving(true);
    try {
      const payload = {
        lines: rows.map(r => ({
          item_id: r.type === 'Inventory' ? r.item_id : null,
          free_text_name: r.type !== 'Inventory' ? r.free_text_name.trim() : null,
          uom: r.uom,
          ordered_qty: Number(r.qty),
          unit_price: Number(r.unit_price),
          type: r.type,
        }))
      };
      const d1b = decodeURIComponentSafe(expenseId);
      const normIdB = String(d1b).replace(/%252F/gi, '%2F').replace(/%2F/gi, '/');
      await api.post(`/api/expenses/items/by-id`, {
        expenseId: normIdB,
        ...payload,
      });
      alert('Items saved');
    } catch (e) {
      console.error('Save items failed', e);
      alert('Failed to save items');
    } finally {
      setItemsSaving(false);
    }
  };

  return (
    <div className="p-6 w-full space-y-6" style={{ color: 'var(--text)' }}>
      <div>
        <div className="flex items-center mb-1">
          <BsFileEarmarkText className="text-2xl mr-2" style={{ color: 'var(--accent)' }} />
          <h1 className="text-2xl font-bold" style={{ color: 'var(--heading-color, var(--text))' }}>
            Vendor & Item Details
          </h1>
        </div>
        <p className="text-sm" style={{ color: 'var(--muted)' }}>
          Expense ID: <span className="font-semibold" style={{ color: 'var(--accent)' }}>{expenseId}</span>
        </p>
      </div>

      <div className="rounded-xl shadow-sm p-6 border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
          <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="flex items-center text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                  <BsBuilding className="mr-2" /> Vendor Name
                </label>
                <input
                  className="w-full py-2 px-3 rounded-md text-sm focus:outline-none"
                  style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)' }}
                  name="vendor_name"
                  value={formData.vendor_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label className="flex items-center text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                  <BsCoin className="mr-2" /> Cost
                </label>
                <div className="flex gap-2">
                  <select
                    className="py-2 px-3 rounded-md text-sm focus:outline-none"
                    style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)' }}
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    title="Currency"
                  >
                    <option value="INR">₹ INR</option>
                    <option value="USD">$ USD</option>
                    <option value="EUR">€ EUR</option>
                    <option value="GBP">£ GBP</option>
                  </select>
                  <input
                    className="w-full py-2 px-3 rounded-md text-sm focus:outline-none"
                    style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)' }}
                    name="cost"
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="flex items-center text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                  <BsCalendarDate className="mr-2" /> Date
                </label>
                <input
                  className="w-full py-2 px-3 rounded-md text-sm focus:outline-none"
                  style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)' }}
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label className="flex items-center text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                  <BsCreditCardFill className="mr-2" /> Payment Terms
                </label>
                <select
                  className="w-full py-2 px-3 rounded-md text-sm focus:outline-none"
                  style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)' }}
                  name="payment_terms"
                  value={formData.payment_terms}
                  onChange={handleChange}
                >
                  <option value="">Select Payment Term</option>
                  <option value="Credit">Credit</option>
                  <option value="Immediate">Immediate</option>
                  <option value="Advance 50%">Advance 50%</option>
                </select>
              </div>
            </div>
            <div>
              <label className="flex items-center text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                <BsPencil className="mr-2" /> Description
              </label>
              <textarea
                className="w-full py-2 px-3 rounded-md text-sm focus:outline-none"
                style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)' }}
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4" // Added rows for a taller textarea
              ></textarea>
            </div>
            <div>
              <label className="flex items-center text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
                <BsPaperclip className="mr-2" /> Quote Attachment (PDF/JPG, max 5MB)
              </label>
              <input
                type="file"
                className="w-full text-sm rounded-md"
                style={{ background: 'var(--panel)', color: 'var(--text)', border: '1px solid var(--border)', padding: '0.5rem 0.75rem' }}
                name="quote"
                accept=".pdf,.jpg,.jpeg"
                onChange={(e) => setFormData(prev => ({ ...prev, quote: e.target.files[0] }))}
              />
            </div>
            <div className="flex gap-4 justify-end pt-4">
              <button
                type="button"
                className="font-semibold py-2 px-6 rounded-md transition border"
                style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                onClick={() => handleSubmit(true)}
              >
                Save & Add New Vendor
              </button>
              <button
                type="button"
                className="font-semibold py-2 px-6 rounded-md transition border"
                style={{ background: '#8c211e', color: '#ffffff', borderColor: '#721a18' }}
                onClick={() => handleSubmit(false)}
              >
                Save
              </button>
            </div>
          </form>
        </div>

        {/* Line Items Section */}
        <div className="rounded-xl shadow-sm p-8 border mt-6" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
          <div className="flex items-center mb-4" style={{ color: 'var(--text)' }}>
            <h3 className="text-lg font-semibold">Expense Line Items</h3>
          </div>
          {itemsLoading ? (
            <div className="text-sm" style={{ color: 'var(--muted)' }}>Loading items…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr style={{ color: 'var(--muted)' }}>
                    <th className="text-left py-2 pr-2">Type</th>
                    <th className="text-left py-2 pr-2">Item</th>
                    <th className="text-left py-2 pr-2">UOM</th>
                    <th className="text-right py-2 pr-2">Qty</th>
                    <th className="text-right py-2 pr-2">Unit Price</th>
                    <th className="text-right py-2 pr-2">Line Total</th>
                    <th className="text-right py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const qty = Number(r.qty) || 0;
                    const price = Number(r.unit_price) || 0;
                    const lineTotal = qty * price;
                    return (
                      <tr key={r.id}>
                        <td className="py-1 pr-2">
                          <select
                            value={r.type}
                            onChange={(e) => updateRow(r.id, { type: e.target.value, item_id: '', free_text_name: '' })}
                            className="border rounded px-2 py-1"
                            style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                          >
                            <option>Inventory</option>
                            <option>Non-Inventory</option>
                          </select>
                        </td>
                        <td className="py-1 pr-2">
                          {r.type === 'Inventory' ? (
                            <div className="flex gap-2 items-center">
                              <select
                                value={r.item_id}
                                onChange={(e) => updateRow(r.id, { item_id: e.target.value })}
                                className="border rounded px-2 py-1 w-64"
                                style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                              >
                                <option value="">Select item…</option>
                                {itemsMaster.map((it) => (
                                  <option key={it.id} value={it.id}>{it.name} {it.sku && it.sku !== it.name ? `(${it.sku})` : ''}</option>
                                ))}
                              </select>
                              <button
                                type="button"
                                className="px-2 py-1 border rounded"
                                style={{ borderColor: 'var(--border)' }}
                                onClick={() => setShowQuickAdd(true)}
                              >
                                + Quick Add
                              </button>
                            </div>
                          ) : (
                            <input
                              value={r.free_text_name}
                              onChange={(e) => updateRow(r.id, { free_text_name: e.target.value })}
                              placeholder="Describe item"
                              className="border rounded px-2 py-1 w-64"
                              style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                            />
                          )}
                        </td>
                        <td className="py-1 pr-2">
                          <input
                            value={r.uom}
                            onChange={(e) => updateRow(r.id, { uom: e.target.value })}
                            className="border rounded px-2 py-1 w-24"
                            style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                          />
                        </td>
                        <td className="py-1 pr-2 text-right">
                          <input
                            type="number"
                            min="0"
                            value={r.qty}
                            onChange={(e) => updateRow(r.id, { qty: e.target.value })}
                            className="border rounded px-2 py-1 w-28 text-right"
                            style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                          />
                        </td>
                        <td className="py-1 pr-2 text-right">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={r.unit_price}
                            onChange={(e) => updateRow(r.id, { unit_price: e.target.value })}
                            className="border rounded px-2 py-1 w-36 text-right"
                            style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                          />
                        </td>
                        <td className="py-1 pr-2 text-right" style={{ color: 'var(--text)' }}>
                          ₹ {lineTotal.toFixed(2)}
                        </td>
                        <td className="py-1 text-right">
                          <button
                            type="button"
                            className="px-2 py-1 border rounded"
                            style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}
                            onClick={() => removeRow(r.id)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4}></td>
                    <td className="py-2 pr-2 text-right font-semibold">Total</td>
                    <td className="py-2 pr-2 text-right font-semibold">₹ {grandTotal.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-2 border rounded"
                  style={{ borderColor: 'var(--border)' }}
                  onClick={addRow}
                >
                  + Add Row
                </button>
                {/* Items are saved together with vendor save above */}
              </div>
            </div>
          )}
        </div>

        {showQuickAdd && (
          <div className="fixed inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.35)' }}>
            <div className="w-full max-w-md p-4 rounded border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
              <h2 className="text-lg font-semibold mb-2">Quick Add Item</h2>
              <form onSubmit={handleQuickAddSubmit} className="space-y-3">
                <div>
                  <label className="text-sm">Name</label>
                  <input
                    className="w-full border rounded px-2 py-1"
                    style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                    value={quickAdd.name}
                    onChange={(e) => setQuickAdd(q => ({ ...q, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-sm">SKU</label>
                    <input
                      className="w-full border rounded px-2 py-1"
                      style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                      value={quickAdd.sku}
                      onChange={(e) => setQuickAdd(q => ({ ...q, sku: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-sm">UOM</label>
                    <input
                      className="w-full border rounded px-2 py-1"
                      style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                      value={quickAdd.uom}
                      onChange={(e) => setQuickAdd(q => ({ ...q, uom: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-sm">Category</label>
                    <select
                      className="w-full border rounded px-2 py-1"
                      style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                      value={quickAdd.category}
                      onChange={(e) => setQuickAdd(q => ({ ...q, category: e.target.value }))}
                    >
                      <option>Stationery</option>
                      <option>House Keeping</option>
                      <option>IT</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm">Reorder Level</label>
                    <input
                      type="number"
                      min="0"
                      className="w-full border rounded px-2 py-1"
                      style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
                      value={quickAdd.reorder_level}
                      onChange={(e) => setQuickAdd(q => ({ ...q, reorder_level: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-2">
                  <button type="button" className="px-3 py-2 border rounded" style={{ borderColor: 'var(--border)' }} onClick={() => setShowQuickAdd(false)}>Cancel</button>
                  <button type="submit" className="px-3 py-2 rounded border font-semibold" style={{ background: '#8c211e', color: '#fff', borderColor: '#721a18' }}>Add</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
  );
}

export default ExpenseDetailsForm;





// import { useState } from 'react';
// import axios from 'axios';
// import { useNavigate, useLocation } from 'react-router-dom';
// import { useParams } from 'react-router-dom';

// function ExpenseDetailsForm() {
//   const navigate = useNavigate();
//   //const expenseId = location.state?.expense_id;
//   const location = useLocation();
//   const { expenseId: expenseIdFromUrl } = useParams();
//   const expenseIdFromState = location.state?.expense_id;

//   const expenseId = expenseIdFromState || expenseIdFromUrl || '';

//   const [formData, setFormData] = useState({
//     vendor_name: '',
//     cost: '',
//     date: '',
//     payment_terms: '',
//     description: ''
//   });

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData(prev => ({ ...prev, [name]: value }));
//   };

//   const handleSubmit = async (stayOnPage = false) => {
//     try {
//       const form = new FormData();
//       form.append('vendor_name', formData.vendor_name);
//       form.append('cost', formData.cost);
//       form.append('date', formData.date);
//       form.append('payment_terms', formData.payment_terms);
//       form.append('description', formData.description);
//       form.append('expense_id', expenseId);
//       if (formData.quote) {
//         form.append('quote', formData.quote);
//       }

//       await axios.post('http://localhost:5000/api/expenses/expense-details', form, {
//         headers: {
//           'Content-Type': 'multipart/form-data'
//         }
//       });

//       alert('Vendor details saved successfully');

//       if (!stayOnPage) navigate('/expenses');
//       else {
//         setFormData({
//           vendor_name: '',
//           cost: '',
//           date: '',
//           payment_terms: '',
//           description: '',
//           quote: null
//         });
//       }

//     } catch (error) {
//       console.error('Error saving vendor details:', error);
//       alert('Failed to save vendor details');
//     }
//   };


//   return (
//     <div className="container mt-4">
//       <h3>Vendor Details for Expense ID: {expenseId}</h3>
//       <form onSubmit={(e) => e.preventDefault()}>
//         <div className="mb-3">
//           <label>Vendor Name</label>
//           <input className="form-control" name="vendor_name" value={formData.vendor_name} onChange={handleChange} required />
//         </div>

//         <div className="mb-3">
//           <label>Cost</label>
//           <input className="form-control" name="cost" type="number" value={formData.cost} onChange={handleChange} required />
//         </div>

//         <div className="mb-3">
//           <label>Date</label>
//           <input className="form-control" name="date" type="date" value={formData.date} onChange={handleChange} required />
//         </div>

//         <div className="mb-3">
//           <label>Payment Terms</label>
//           <select className="form-control" name="payment_terms" value={formData.payment_terms} onChange={handleChange}>
//             <option value="">Select Payment Term</option> {/* Optional: A default unselected option */}
//             <option value="Credit">Credit</option>
//             <option value="Immediate">Immediate</option>
//             <option value="Advance 50%">Advance 50%</option>
//           </select>
//         </div>

//         <div className="mb-3">
//           <label>Description</label>
//           <textarea className="form-control" name="description" value={formData.description} onChange={handleChange}></textarea>
//         </div>

//         <div className="mb-3">
//           <label>Quote Attachment (PDF/JPG, max 5MB)</label>
//           <input
//             type="file"
//             className="form-control"
//             name="quote"
//             accept=".pdf,.jpg,.jpeg"
//             onChange={(e) => setFormData(prev => ({ ...prev, quote: e.target.files[0] }))}
//           />
//         </div>

//         <button className="btn btn-primary me-2" onClick={() => handleSubmit(false)}>Save</button>
//         <button className="btn btn-secondary" onClick={() => handleSubmit(true)}>Save & Add New Vendor</button>
//       </form>
//     </div>
//   );
// }

// export default ExpenseDetailsForm;
